import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { ArrowLeft, Bell, Download, ImagePlus, Mail, MessageCircle, RefreshCw, Search, Send, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { openInstallPrompt } from "../../components/InstallPrompt";
import { siteConfig } from "../../lib/siteConfig";
import ImagePreviewModal from "../../components/ImagePreviewModal";
import LinkifiedText from "../../components/LinkifiedText";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import VerifiedName from "../../components/VerifiedName";
import { readChatImage } from "../../lib/imageUpload";
import ChatImageAttachment from "../../components/ChatImageAttachment";

function isPhoneLikeDevice() {
  if (typeof window === "undefined") return false;
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
  const touchPoints = navigator.maxTouchPoints || 0;
  const screenWidth = window.screen?.width || window.innerWidth;
  const screenHeight = window.screen?.height || window.innerHeight;
  const smallestScreenSide = Math.min(screenWidth, screenHeight);
  return Boolean((coarsePointer || touchPoints > 0) && smallestScreenSide <= 820);
}

export default function AdminSupport({ appMode = false }) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [phoneLayout, setPhoneLayout] = useState(() => isPhoneLikeDevice());
  const [unreadCount, setUnreadCount] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => (
    typeof window !== "undefined" && window.localStorage.getItem("admin_support_notifications") === "on"
  ));
  const scrollerRef = useRef(null);
  const fileRef = useRef(null);
  const seenCustomerMessagesRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = useCallback(async (selectedOverride = selectedId) => {
    const params = selectedOverride ? { selected_id: selectedOverride } : undefined;
    const { data } = await api.get("/admin/support", params ? { params } : undefined);
    const list = Array.isArray(data) ? data : [];
    const newCustomerMessages = [];
    const totalUnread = totalTicketUnreadCount(list);

    list.forEach((ticket) => {
      normalizeMessages(ticket).forEach((message) => {
        if (message.sender !== "user") return;
        const key = messageKey(ticket, message);
        if (!seenCustomerMessagesRef.current.has(key)) {
          seenCustomerMessagesRef.current.add(key);
          if (!firstLoadRef.current) newCustomerMessages.push({ ticket, message });
        }
      });
    });

    if (firstLoadRef.current) {
      firstLoadRef.current = false;
    } else if (newCustomerMessages.length) {
      if (notificationsEnabled) {
        notifyAdmin(newCustomerMessages[0].ticket, newCustomerMessages[0].message, newCustomerMessages.length);
      }
    }

    setTickets(list);
    setUnreadCount(totalUnread);
    updateSupportTitle(totalUnread);
    setSelectedId((current) => (current && list.some((ticket) => ticket.id === current) ? current : null));
    setLastSync(new Date());
  }, [notificationsEnabled, selectedId]);

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, [load]);

  useAdminAutoRefresh(() => load());

  useEffect(() => {
    const updatePhoneLayout = () => setPhoneLayout(isPhoneLikeDevice());
    updatePhoneLayout();
    const pointerQuery = window.matchMedia?.("(pointer: coarse)");
    pointerQuery?.addEventListener?.("change", updatePhoneLayout);
    window.addEventListener("resize", updatePhoneLayout);
    window.addEventListener("orientationchange", updatePhoneLayout);
    return () => {
      pointerQuery?.removeEventListener?.("change", updatePhoneLayout);
      window.removeEventListener("resize", updatePhoneLayout);
      window.removeEventListener("orientationchange", updatePhoneLayout);
    };
  }, []);

  useEffect(() => {
    return () => updateSupportTitle(0);
  }, []);

  const selected = useMemo(
    () => tickets.find((ticket) => ticket.id === selectedId) || null,
    [tickets, selectedId],
  );

  const visibleTickets = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tickets.filter((ticket) => {
      if (unreadOnly && ticketUnreadCount(ticket) <= 0) return false;
      if (!term) return true;
      const haystack = [
        customerName(ticket),
        displayEmail(ticket.email),
        ticket.subject,
        ticket.guest_id,
        ticket.status,
        messagePreview(ticket),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [tickets, search, unreadOnly]);

  const messages = useMemo(() => normalizeMessages(selected), [selected]);
  const threadOpen = Boolean(selected && mobileThreadOpen);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [selectedId, messages.length]);

  const markTicketRead = useCallback(async (ticket) => {
    const ticketId = ticket?.id;
    const ticketUnread = ticketUnreadCount(ticket);
    if (!ticketId || ticketUnread <= 0) return;

    const readAt = new Date().toISOString();
    setTickets((current) => current.map((row) => (
      row.id === ticketId ? { ...row, unread_count: 0, admin_last_read_at: readAt } : row
    )));
    setUnreadCount((current) => {
      const next = Math.max(0, current - ticketUnread);
      updateSupportTitle(next);
      return next;
    });

    try {
      const { data } = await api.post(`/admin/support/${ticketId}/read`, {}, { skipMaintenance: true });
      setTickets((current) => current.map((row) => (row.id === data.id ? data : row)));
    } catch {
      load().catch(() => {});
    }
  }, [load]);

  useEffect(() => {
    if (selected && mobileThreadOpen && document.visibilityState !== "hidden" && ticketUnreadCount(selected) > 0) {
      markTicketRead(selected);
    }
  }, [markTicketRead, selected, mobileThreadOpen]);

  const enableNotifications = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      toast.error("Browser notifications are not supported here.");
      return;
    }
    try {
      const permission = window.Notification.permission === "granted"
        ? "granted"
        : await window.Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("Please allow notifications in the browser to get support alerts.");
        return;
      }
      window.localStorage.setItem("admin_support_notifications", "on");
      setNotificationsEnabled(true);
      playAlertTone();
      toast.success("Support alerts enabled");
    } catch {
      toast.error("Could not enable browser alerts.");
    }
  };

  const sendReply = async () => {
    const message = reply.trim();
    const picked = attachment;
    if ((!message && !picked) || !selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/support/${selected.id}/messages`, {
        message,
        image_url: picked?.dataUrl,
        image_name: picked?.name,
      });
      setReply("");
      setAttachment(null);
      setSelectedId(data.id);
      setTickets((current) => current.map((ticket) => (ticket.id === data.id ? data : ticket)));
      await load();
      toast.success("Reply sent");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const selectImage = async (file) => {
    if (!file) return;
    try {
      setAttachment(await readImage(file));
    } catch (e) {
      toast.error(e.message);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const pasteImage = async (event) => {
    const file = clipboardImageFile(event);
    if (!file) return;
    event.preventDefault();
    await selectImage(file);
  };

  return (
    <div className={`admin-support-app flex h-[100dvh] min-h-0 max-h-[100dvh] flex-col overflow-hidden bg-[#08090d] text-white ${phoneLayout ? "admin-support-phone-layout" : ""}`}>
      <header className="shrink-0 border-b border-white/10 bg-[#090a0f] px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-3">
              {threadOpen && (
                <button
                  type="button"
                  onClick={() => setMobileThreadOpen(false)}
                  className="admin-support-mobile-back grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5 md:hidden"
                  aria-label="Back to conversations"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <MessageCircle className="h-7 w-7 shrink-0 text-[#F6C90E]" />
              <h1 className="truncate text-2xl font-semibold tracking-tight sm:text-3xl">
                {appMode ? "Sophia Support" : "Chat"}
              </h1>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                Live
              </span>
              {appMode && (
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#F5D76E]">
                  <ShieldCheck className="h-3 w-3" />
                  Admin only
                </span>
              )}
              {unreadCount > 0 && (
                <span className="inline-flex shrink-0 items-center rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/15 px-2.5 py-1 text-xs text-[#F5D76E]">
                  {unreadCount} new
                </span>
              )}
            </div>
            <p className="mt-1 hidden truncate text-sm text-slate-500 sm:block">
              {appMode ? "Standalone admin support app" : `${tickets.length} conversations`}
              {appMode ? ` - ${tickets.length} conversations` : ""}
              {lastSync ? ` - synced ${lastSync.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}` : ""}
            </p>
          </div>

          <div className="flex max-w-full items-center gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              aria-pressed={unreadOnly}
              onClick={() => {
                setUnreadOnly((current) => !current);
                setMobileThreadOpen(false);
                setSelectedId(null);
              }}
              className={`shrink-0 rounded-full border px-3 py-2 text-xs font-medium ${unreadOnly ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F5D76E]" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
            >
              Unread ({unreadCount})
            </button>
            {appMode && (
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                className="shrink-0 rounded-full border border-white/10 px-3 py-2 text-xs font-medium text-slate-300 transition hover:bg-white/5"
                title="Back to admin"
              >
                <span className="inline-flex items-center gap-2">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={enableNotifications}
              className={`shrink-0 rounded-full border px-3 py-2 text-xs font-medium transition ${
                notificationsEnabled
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                  : "border-white/10 text-slate-300 hover:bg-white/5"
              }`}
              title="Enable browser alerts"
            >
              <span className="inline-flex items-center gap-2">
                <Bell className="h-3.5 w-3.5" />
                {notificationsEnabled ? "Alerts on" : "Enable alerts"}
              </span>
            </button>
            {appMode && (
              <button
                type="button"
                onClick={openInstallPrompt}
                className="hidden shrink-0 rounded-full border border-[#D4AF37]/30 px-3 py-2 text-xs font-medium text-[#F5D76E] transition hover:bg-[#D4AF37]/10 sm:inline-flex"
                title="Install this admin app"
              >
                <span className="inline-flex items-center gap-2">
                  <Download className="h-3.5 w-3.5" />
                  Install
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => load().catch((e) => toast.error(formatApiError(e)))}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5"
              title="Refresh now"
              aria-label="Refresh conversations"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="admin-support-panels grid min-h-0 flex-1 overflow-hidden md:grid-cols-[398px_minmax(0,1fr)]">
        <aside
          data-thread-open={threadOpen ? "true" : "false"}
          className={`admin-support-sidebar ${threadOpen ? "hidden" : "flex"} min-h-0 min-w-0 flex-col overflow-hidden border-r border-white/10 bg-[#0a0b10] md:flex`}
        >
          <div className="shrink-0 border-b border-white/10 p-3 sm:p-4">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 w-full rounded-full border border-white/10 bg-black/20 pl-10 pr-4 text-sm outline-none placeholder:text-slate-500 focus:border-[#D4AF37]/50 sm:h-11"
                placeholder="Search players..."
              />
            </label>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[calc(4.5rem+env(safe-area-inset-bottom))] sm:pb-0">
            {visibleTickets.map((ticket) => {
              const active = ticket.id === selectedId;
              return (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => {
                    setSelectedId(ticket.id);
                    setMobileThreadOpen(true);
                    markTicketRead(ticket);
                    load(ticket.id).catch(() => {});
                  }}
                  data-testid={`ticket-row-${ticket.id}`}
                  className={`w-full border-b border-white/[0.06] px-3 py-4 text-left transition sm:px-4 ${
                    active ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <Avatar ticket={ticket} />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <VerifiedName
                            user={ticket}
                            name={customerName(ticket)}
                            className="min-w-0 text-[15px] font-semibold leading-tight text-white"
                          />
                          <PresenceIndicator ticket={ticket} compact />
                        </div>
                        <span className="shrink-0 text-[11px] font-medium text-slate-500">
                          {formatRelativeTime(ticket.last_message_at || ticket.updated_at || ticket.created_at)}
                        </span>
                      </div>
                      <p className="mt-1 truncate text-sm text-slate-400">{messagePreview(ticket)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
            {visibleTickets.length === 0 && (
              <div className="grid min-h-[280px] place-items-center px-6 text-center text-sm text-slate-500">
                {unreadOnly ? "No unread conversations found." : "No player conversations found."}
              </div>
            )}
          </div>
        </aside>

        <main
          data-thread-open={threadOpen ? "true" : "false"}
          className={`admin-support-thread ${threadOpen ? "flex" : "hidden md:flex"} min-h-0 min-w-0 flex-col overflow-hidden bg-[#090a0f]`}
        >
          {selected ? (
            <ConversationPanel
              attachment={attachment}
              busy={busy}
              fileRef={fileRef}
              messages={messages}
              onImagePreview={setImagePreview}
              reply={reply}
              scrollerRef={scrollerRef}
              selected={selected}
              selectImage={selectImage}
              sendReply={sendReply}
              setAttachment={setAttachment}
              setMobileThreadOpen={setMobileThreadOpen}
              setReply={setReply}
              pasteImage={pasteImage}
            />
          ) : (
            <EmptyConversation />
          )}
        </main>
      </div>
      <ImagePreviewModal image={imagePreview} onClose={() => setImagePreview(null)} />
    </div>
  );
}

function ConversationPanel({
  attachment,
  busy,
  fileRef,
  messages,
  onImagePreview,
  reply,
  scrollerRef,
  selected,
  selectImage,
  sendReply,
  setAttachment,
  setMobileThreadOpen,
  setReply,
  pasteImage,
}) {
  return (
    <>
      <div className="shrink-0 border-b border-white/10 bg-[#0c0d12] px-4 py-3">
        <div className="flex min-w-0 items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileThreadOpen(false)}
              className="admin-support-mobile-back grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5 md:hidden"
              aria-label="Back to conversations"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <Avatar ticket={selected} />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <h2 className="min-w-0 text-lg font-semibold">
                  <VerifiedName user={selected} name={customerName(selected)} className="max-w-[20rem]" tickClassName="h-5 w-5" />
                </h2>
                <PresenceIndicator ticket={selected} />
              </div>
              <p className="mt-0.5 flex min-w-0 items-center gap-1.5 truncate text-xs text-slate-500">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{displayEmail(selected.email)}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div ref={scrollerRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6">
        {messages.length ? (
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
            {messages.map((msg) => <ChatBubble key={msg.id || `${msg.sender}-${msg.created_at}-${msg.message}`} msg={msg} onImagePreview={onImagePreview} />)}
          </div>
        ) : (
          <div className="grid h-full place-items-center text-center text-sm text-slate-500">
            No messages yet.
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-white/10 bg-[#0c0d12] p-3 sm:p-4">
        <div className="mx-auto w-full max-w-5xl">
          {attachment && (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 p-2">
              <img src={attachment.dataUrl} alt="" className="h-10 w-10 rounded-lg object-cover" />
              <span className="min-w-0 flex-1 truncate text-xs text-slate-300">{attachment.name}</span>
              <button type="button" onClick={() => setAttachment(null)} className="rounded-full p-1.5 hover:bg-white/10" aria-label="Remove image">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-end gap-2" onPaste={pasteImage}>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => selectImage(e.target.files?.[0])} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              title="Add image"
              aria-label="Add image"
              className="grid h-[54px] w-[54px] shrink-0 place-items-center rounded-2xl border border-white/10 bg-black/30 text-slate-200 hover:bg-white/5 disabled:opacity-50"
            >
              <ImagePlus className="h-4 w-4" />
            </button>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendReply();
                }
              }}
              onPaste={pasteImage}
              rows={2}
              data-testid="ticket-reply"
              className="min-w-0 flex-1 resize-none rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none placeholder:text-slate-500 focus:border-[#D4AF37]/50"
              placeholder="Type a message..."
            />
            <button
              type="button"
              onClick={sendReply}
              disabled={busy || (!reply.trim() && !attachment)}
              data-testid="send-agent-reply"
              className="grid h-[54px] w-[58px] shrink-0 place-items-center rounded-2xl bg-[#D4AF37] text-black hover:bg-[#e6c348] disabled:opacity-50"
              aria-label="Send reply"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function EmptyConversation() {
  return (
    <div className="grid min-h-0 flex-1 place-items-center px-6 text-center">
      <div className="max-w-md text-slate-400">
        <MessageCircle className="mx-auto mb-4 h-10 w-10 text-slate-700" />
        <p className="text-lg leading-relaxed">
          Select a player conversation on the left - or wait for the first message.
          Posts you send go out as {siteConfig.name} support.
        </p>
      </div>
    </div>
  );
}

function Avatar({ ticket }) {
  const src = avatarUrl(ticket);
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="h-11 w-11 shrink-0 rounded-full border border-[#D4AF37]/30 object-cover"
      />
    );
  }
  return (
    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#3a280a] text-sm font-bold text-[#F6C90E]">
      {initialFor(ticket)}
    </div>
  );
}

function PresenceIndicator({ ticket, compact = false }) {
  const online = isTicketRecentlyActive(ticket);
  const label = online ? "Online" : "Offline";
  return (
    <span
      title={label}
      aria-label={label}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border ${
        online
          ? "border-emerald-400/35 bg-emerald-500/12 text-emerald-300"
          : "border-red-400/35 bg-red-500/10 text-red-300"
      } ${compact ? "h-2.5 w-2.5 justify-center p-0" : "px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]"}`}
    >
      <span className={`rounded-full ${online ? "bg-emerald-400" : "bg-red-400"} ${compact ? "h-2 w-2" : "h-1.5 w-1.5"}`} />
      {!compact && label}
    </span>
  );
}

function normalizeMessages(ticket) {
  if (!ticket) return [];
  if (Array.isArray(ticket.messages) && ticket.messages.length) return ticket.messages;
  const out = [];
  if (ticket.message) {
    out.push({ id: `${ticket.id}-user`, sender: "user", message: ticket.message, created_at: ticket.created_at });
  }
  if (ticket.admin_reply) {
    out.push({ id: `${ticket.id}-agent`, sender: "agent", message: ticket.admin_reply, created_at: ticket.updated_at || ticket.created_at });
  }
  return out;
}

function lastMessage(ticket) {
  const messages = normalizeMessages(ticket);
  return messages[messages.length - 1] || null;
}

function messagePreview(ticket) {
  const last = lastMessage(ticket);
  if (!last) return ticket?.subject || "No messages yet";
  const prefix = last.sender === "agent" ? (last.agent_target ? `You -> ${last.agent_target}: ` : "You: ") : "";
  const target = last.sender !== "agent" && last.agent_target ? `To ${last.agent_target}: ` : "";
  if (last.image_url && !last.message) return `${prefix}${target}Photo`;
  return `${prefix}${target}${last.message || "Photo"}`;
}

function messageKey(ticket, message) {
  return message.id || `${ticket.id}:${message.sender}:${message.created_at || ""}:${message.message || ""}:${message.image_url || ""}`;
}

function ticketUnreadCount(ticket) {
  const count = Number(ticket?.unread_count || 0);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
}

function totalTicketUnreadCount(tickets) {
  return (Array.isArray(tickets) ? tickets : []).reduce((total, ticket) => total + ticketUnreadCount(ticket), 0);
}

function isTicketRecentlyActive(ticket) {
  const messages = normalizeMessages(ticket);
  const latestCustomerMessage = [...messages].reverse().find((message) => message?.sender === "user");
  const value = latestCustomerMessage?.created_at || ticket?.created_at;
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() <= 5 * 60 * 1000;
}

function updateSupportTitle(count) {
  if (typeof document === "undefined") return;
  document.title = count > 0 ? `(${count}) Chat - ${siteConfig.name}` : siteConfig.name;
}

function notifyAdmin(ticket, message, count) {
  playAlertTone();
  if (typeof window === "undefined" || !("Notification" in window) || window.Notification.permission !== "granted") return;
  try {
    const customer = customerName(ticket);
    const body = message.message?.trim() || (message.image_url ? "Sent a photo" : "New support message");
    const notification = new window.Notification(
      count > 1 ? `${count} new support messages` : `New message from ${customer}`,
      {
        body: body.slice(0, 140),
        icon: "/icon-192.png",
        tag: `support-${ticket.id}`,
      },
    );
    notification.onclick = () => {
      window.focus();
      window.location.assign("/admin/support-app");
    };
  } catch {
    // Browser notifications can fail silently in some mobile shells.
  }
}

function playAlertTone() {
  if (typeof window === "undefined") return;
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.25);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.28);
    setTimeout(() => context.close().catch(() => {}), 450);
  } catch {
    // Audio is best-effort; notifications still work without it.
  }
}

function displayEmail(email) {
  if (!email || email.endsWith(`@${siteConfig.guestEmailDomain}`)) return "Guest visitor";
  return email;
}

function customerName(ticket) {
  if (!ticket) return "Guest";
  return ticket.name || ticket.username || displayEmail(ticket.email) || "Guest";
}

function avatarUrl(ticket) {
  if (!ticket) return "";
  return ticket.avatar_url || ticket.profile_image || ticket.profile_pic || ticket.photo_url || "";
}

function initialFor(ticket) {
  const name = customerName(ticket).trim();
  return (name[0] || "G").toUpperCase();
}

function formatRelativeTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function ChatBubble({ msg, onImagePreview }) {
  const isAgent = msg.sender === "agent";
  const target = msg.agent_target ? String(msg.agent_target).toUpperCase() : "";
  return (
    <div className={`flex ${isAgent ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[86%] rounded-[22px] border px-4 py-3 text-sm sm:max-w-[72%] ${
        isAgent
          ? "rounded-br-md border-[#D4AF37] bg-[#D4AF37] text-black"
          : "rounded-bl-md border-white/10 bg-white/[0.07] text-white"
      }`}>
        <div className={`mb-1 text-[10px] ${isAgent ? "text-black/55" : "text-white/45"}`}>
          {isAgent ? (target ? `Agent -> ${target}` : "Agent") : target ? `Customer -> ${target}` : "Customer"}
        </div>
        {msg.image_url && msg.image_url !== "__image__" && (
          <ChatImageAttachment
            src={msg.image_url}
            name={msg.image_name || "Attachment"}
            onPreview={onImagePreview}
            className="max-h-64 w-full rounded-xl object-contain"
          />
        )}
        {msg.message && <LinkifiedText text={msg.message} linkClassName={isAgent ? "text-black" : "text-[#F8E08E]"} />}
        {msg.created_at && (
          <div className={`mt-2 text-right text-[10px] ${isAgent ? "text-black/45" : "text-white/35"}`}>
            {formatRelativeTime(msg.created_at)}
          </div>
        )}
      </div>
    </div>
  );
}

function readImage(file) {
  return readChatImage(file);
}

function clipboardImageFile(event) {
  if (event.defaultPrevented) return null;
  const items = Array.from(event.clipboardData?.items || []);
  const item = items.find((entry) => entry.kind === "file" && entry.type?.startsWith("image/"));
  const file = item?.getAsFile();
  if (!file) return null;
  const extension = file.type === "image/jpeg" ? "jpg" : (file.type.split("/")[1] || "png");
  return new File([file], file.name || `pasted-screenshot-${Date.now()}.${extension}`, { type: file.type || "image/png" });
}
